/* ---------------------------------------------------------------------------
 * MD Fashionwear — wishlist page renderer
 *
 * PREORDER IS A LEBANON-ONLY IDEA. READ THIS BEFORE EDITING.
 * Worldwide shoppers buy from the overseas catalogue, and those products all
 * carry the preorder tag internally — but that word is a warehouse detail, not
 * something a worldwide customer should ever read. Outside Lebanon the wishlist
 * must say "Buy it now", show no preorder badge, and mark no size or swatch as
 * preorder. Every preorder path in this file is therefore gated on
 * `market === 'LB'`, and the gate lives in exactly three places:
 *     twinHas()   — returns false off-LB, which kills amber sizes and swatches
 *     paint()     — `preorder` cannot be true off-LB
 *     build()     — the twin is not even looked up off-LB
 * This was shipped broken once: `preorder` was `st.useTwin || isPreorderProduct(p)`
 * with no market check, so Albania saw "PREORDER NOW" on every row. Do not
 * remove these gates.
 *
 * SIZES THE LOCAL WAREHOUSE HAS RUN OUT OF ARE STILL ORDERABLE — IN LEBANON
 * Many pieces exist twice: local Lebanon stock (tagged ONLINE) and an overseas
 * preorder twin (tagged preorder). If the local product is out of a size, the
 * twin usually still has it. Those sizes stay clickable, and choosing one
 * switches the row to preorder: the badge reads Preorder 15-20 business days,
 * Buy it now becomes Preorder now, and the variant, price and image all come
 * from the twin. MDTwin owns the matching.
 *
 * Sizes with no source at all stay struck through. Of 1,037 titles, 273 are
 * local only and have no preorder twin, so those genuinely cannot be offered.
 *
 * THE PICTURE HAS TO CHANGE WITH THE COLOUR, INSTANTLY
 * Two things used to make that feel wrong. First, the image was taken from the
 * active variant alone, so if the exact colour+size variant carried no photo the
 * picture silently stayed on the previous colour. imageForColor() now falls back
 * to any variant of that colour before giving up on the product photo. Second,
 * swapping src fetched the photo at click time, so the swatch felt slow. Every
 * colour's photo is now preloaded on idle after the first paint, which makes the
 * swap come from cache.
 *
 * PRUNING: a saved handle is only dropped on a hard 404. Any other failure keeps
 * the item, so a blip cannot wipe someone's wishlist.
 * ------------------------------------------------------------------------- */

(function () {
  'use strict';

  var W = window.MDWishlist;
  if (!W) return;

  function C() { return window.MDWishlistCache || null; }

  var state = {};
  var order = [];
  var token = 0;
  var market = 'WW';
  var preloaded = {};

  var SVG_X = '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18"/></svg>';
  var SVG_BAG = '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M6 7h12l-1 13H7L6 7zM9 7V5a3 3 0 016 0v2"/></svg>';
  var SVG_CHECK = '<svg viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="1.9"><circle cx="12" cy="12" r="9"/><path d="M8 12.5l2.5 2.5L16 9.5"/></svg>';
  var SVG_CLOCK = '<svg viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="1.9"><circle cx="12" cy="12" r="9"/><path d="M12 7.5V12l3 2"/></svg>';

  /* ------------------------------------------------------------- helpers */

  function el(tag, cls, html) {
    var n = document.createElement(tag);
    if (cls) n.className = cls;
    if (html != null) n.innerHTML = html;
    return n;
  }

  function norm(s) { return String(s || '').toLowerCase().replace(/[^a-z]/g, ''); }

  function isLB() { return market === 'LB'; }

  function isPreorderProduct(p) {
    var tags = p.tags || [];
    for (var i = 0; i < tags.length; i++) if (norm(tags[i]) === 'preorder') return true;
    return false;
  }

  // MD 7 Sep 2026 - market gate. A wishlist saved before a product became Lebanon-only
  // still holds its handle, and this page rebuilds every row from products.json on the
  // client, so the Liquid gate in snippets/md-market-visibility.liquid cannot reach it.
  // The rule is repeated here. The handle is left in storage untouched: the same browser
  // must still see the row if it comes back from Lebanon.
  // Shapewear is NOT affected - it carries the 'shapewear' tag, not 'Sleepwear'.
  function isHiddenInMarket(p) {
    if (isLB()) return false;
    var tags = p.tags || [];
    for (var i = 0; i < tags.length; i++) {
      var t = norm(tags[i]);
      if (t === 'online' || t === 'sleepwear' || t === 'sleepwears') return true;
    }
    return false;
  }

  function optPos(product, needles) {
    var opts = product.options || [];
    for (var i = 0; i < opts.length; i++) {
      var o = opts[i];
      var name = String((typeof o === 'string' ? o : o.name) || '').toLowerCase();
      for (var j = 0; j < needles.length; j++) {
        if (name.indexOf(needles[j]) > -1) return (o && o.position) || (i + 1);
      }
    }
    return 0;
  }

  function optValues(product, pos) {
    var opts = product.options || [];
    for (var i = 0; i < opts.length; i++) {
      var o = opts[i];
      if (((o && o.position) || (i + 1)) === pos) return (o && o.values) || [];
    }
    return [];
  }

  function variantById(product, id) {
    var v = (product && product.variants) || [];
    for (var i = 0; i < v.length; i++) if (String(v[i].id) === String(id)) return v[i];
    return null;
  }

  function firstSellable(product) {
    var v = (product && product.variants) || [];
    for (var i = 0; i < v.length; i++) if (v[i].available) return v[i];
    return v[0] || null;
  }

  function cents(v) {
    if (v == null) return 0;
    if (typeof v === 'number') return v;
    var s = String(v);
    if (s.indexOf('.') > -1) return Math.round(parseFloat(s) * 100);
    return parseInt(s, 10) || 0;
  }

  function sized(url, w) {
    if (!url) return '';
    return url + (url.indexOf('?') > -1 ? '&' : '?') + 'width=' + w;
  }

  /* The photo for the colour on screen. The active variant wins, but a variant
     without its own photo must not leave the previous colour showing, so any
     variant of the same colour is used before falling back to the product. */
  function imageForColor(product, colorPos, color, variant) {
    if (variant && variant.featured_image && variant.featured_image.src) {
      return variant.featured_image.src;
    }
    if (colorPos && color) {
      var vs = (product && product.variants) || [];
      for (var i = 0; i < vs.length; i++) {
        if (vs[i]['option' + colorPos] === color &&
            vs[i].featured_image && vs[i].featured_image.src) {
          return vs[i].featured_image.src;
        }
      }
    }
    return (product && product.featured_image) || '';
  }

  /* Warm every colour's photo once, on idle, so clicking a swatch is a cache
     hit rather than a network round trip. Runs after the first paint so it
     never competes with the image the shopper is already looking at. */
  function preloadColours(product) {
    var run = function () {
      (product.variants || []).forEach(function (v) {
        var src = v.featured_image && v.featured_image.src;
        if (!src) return;
        var u = sized(src, 500);
        if (preloaded[u]) return;
        preloaded[u] = 1;
        var im = new Image();
        im.decoding = 'async';
        im.src = u;
      });
    };
    if (window.requestIdleCallback) window.requestIdleCallback(run, { timeout: 2000 });
    else setTimeout(run, 300);
  }

  function swatchStyle(name, imgUrl) {
    var key = String(name || '').trim().toLowerCase();
    var map = window.MD_WISHLIST_COLORS || {};
    if (map[key]) return { kind: 'color', value: map[key] };

    var probe = document.createElement('span');
    probe.style.color = '';
    probe.style.color = key.replace(/\s+/g, '');
    if (probe.style.color) return { kind: 'color', value: probe.style.color };

    if (imgUrl) return { kind: 'image', value: sized(imgUrl, 40) };
    return { kind: 'color', value: '#dddad6' };
  }

  /* -------------------------------------------------------------- markup */

  function skeleton(handle) {
    var row = el('div', 'mdw-row is-loading');
    row.setAttribute('data-handle', handle);
    row.innerHTML =
      '<div class="mdw-row__media"><span class="mdw-row__img"></span></div>' +
      '<div class="mdw-row__body">' +
        '<div class="mdw-skel mdw-skel--title"></div>' +
        '<div class="mdw-skel mdw-skel--price"></div>' +
        '<div class="mdw-skel mdw-skel--line"></div>' +
      '</div>';
    return row;
  }

  function removeButton(handle, label) {
    var x = el('button', 'mdw-icon-btn mdw-icon-btn--x', SVG_X);
    x.type = 'button';
    x.setAttribute('data-wishlist-remove', handle);
    x.setAttribute('aria-label', mdT('md_wishlist_page.remove_from_wishlist', 'Remove {title} from wishlist').replace('{title}', label || mdT('md_wishlist_page.item', 'item')));
    return x;
  }

  function stub(row, handle, message) {
    row.className = 'mdw-row mdw-row--stub';
    row.setAttribute('data-handle', handle);
    row.innerHTML = '';

    var media = el('div', 'mdw-row__media');
    media.appendChild(el('span', 'mdw-row__num', String(order.indexOf(handle) + 1)));
    var link = el('a', 'mdw-row__img');
    link.href = '/products/' + handle;
    media.appendChild(link);
    row.appendChild(media);

    var body = el('div', 'mdw-row__body');
    var top = el('div', 'mdw-row__top');
    var title = el('a', 'mdw-row__title');
    title.href = '/products/' + handle;
    title.textContent = handle.replace(/-/g, ' ');
    top.appendChild(title);
    var tools = el('div', 'mdw-row__tools');
    tools.appendChild(removeButton(handle, ''));
    top.appendChild(tools);
    body.appendChild(top);

    var msg = el('p', 'mdw-row__opt');
    msg.textContent = message;
    body.appendChild(msg);

    var actions = el('div', 'mdw-actions');
    var view = el('a', 'mdw-btn mdw-btn--ghost');
    view.href = '/products/' + handle;
    view.innerHTML = mdT('md_wishlist_page.view_product', 'View product') + ' ' + SVG_BAG;
    actions.appendChild(view);
    body.appendChild(actions);

    row.appendChild(body);
  }

  function build(row, product) {
    var handle = product.handle;

    // Not in this market: drop the row entirely rather than drawing an unbuyable card.
    if (isHiddenInMarket(product)) {
      if (row && row.parentNode) row.parentNode.removeChild(row);
      delete state[handle];
      renumber();
      updateSummary();
      return;
    }

    var colorPos = optPos(product, ['color', 'colour']);
    var sizePos = optPos(product, ['size']);
    var start = variantById(product, (state[handle] || {}).variantId) || firstSellable(product);

    state[handle] = {
      product: product,
      twin: (state[handle] || {}).twin || null,
      variantId: start ? start.id : null,
      color: colorPos && start ? start['option' + colorPos] : null,
      size: sizePos && start ? start['option' + sizePos] : null,
      mode: (isLB() && isPreorderProduct(product)) ? 'preorder' : 'stock',
      useTwin: false
    };

    row.className = 'mdw-row';
    row.setAttribute('data-handle', handle);
    row.innerHTML = '';

    var media = el('div', 'mdw-row__media');
    media.appendChild(el('span', 'mdw-row__num', String(order.indexOf(handle) + 1)));
    var imgLink = el('a', 'mdw-row__img');
    imgLink.href = '/products/' + handle;
    var img = document.createElement('img');
    img.alt = product.title || '';
    img.loading = 'eager';
    img.decoding = 'async';
    imgLink.appendChild(img);
    media.appendChild(imgLink);
    row.appendChild(media);

    var body = el('div', 'mdw-row__body');

    var top = el('div', 'mdw-row__top');
    var title = el('a', 'mdw-row__title');
    title.href = '/products/' + handle;
    title.textContent = product.title || '';
    top.appendChild(title);
    var tools = el('div', 'mdw-row__tools');
    tools.appendChild(removeButton(handle, product.title));
    top.appendChild(tools);
    body.appendChild(top);

    body.appendChild(el('p', 'mdw-row__price'));

    if (colorPos) {
      var colorRow = el('p', 'mdw-row__opt');
      colorRow.appendChild(document.createTextNode(mdT('md_wishlist_page.color_label', 'Color:') + ' '));
      colorRow.appendChild(el('span', 'mdw-row__optval mdw-row__color'));
      body.appendChild(colorRow);
      body.appendChild(el('div', 'mdw-colors'));
    }

    if (sizePos) {
      var sizeRow = el('p', 'mdw-row__opt');
      sizeRow.appendChild(document.createTextNode(mdT('md_wishlist_page.size_label', 'Size:') + ' '));
      sizeRow.appendChild(el('span', 'mdw-row__optval mdw-row__size'));
      body.appendChild(sizeRow);
      body.appendChild(el('div', 'mdw-sizes'));
    }

    /* The stock line is Lebanon's delivery promise. Worldwide has its own
       shipping messaging elsewhere and must not see this at all. */
    if (isLB()) body.appendChild(el('p', 'mdw-stock'));

    var actions = el('div', 'mdw-actions');
    var addBtn = el('button', 'mdw-btn mdw-btn--ghost mdw-add', mdT('md_wishlist_page.add_to_cart', 'Add to cart') + ' ' + SVG_BAG);
    addBtn.type = 'button';
    var buyBtn = el('button', 'mdw-btn mdw-btn--solid mdw-buy', mdT('md_wishlist_page.buy_it_now', 'Buy it now'));
    buyBtn.type = 'button';
    actions.appendChild(addBtn);
    actions.appendChild(buyBtn);
    body.appendChild(actions);

    row.appendChild(body);
    row.__meta = { colorPos: colorPos, sizePos: sizePos };

    paint(row);
    preloadColours(product);

    /* Lebanon only: find the preorder twin, then repaint so sold-out sizes open
       up. Off-LB this is skipped entirely — no lookup, no request, no wording. */
    if (isLB() && window.MDTwin && !state[handle].twin) {
      window.MDTwin.twinFor(product).then(function (twin) {
        if (!twin || !state[handle]) return;
        state[handle].twin = twin;
        preloadColours(twin);
        paint(row);
      }).catch(function () {});
    }
  }

  /* --------------------------------------------------------------- paint */

  // The variant the shopper is actually buying: local, or the twin's.
  function activeVariant(st) {
    if (st.useTwin && st.twin) {
      return window.MDTwin.findVariant(st.twin, st.color, st.size);
    }
    return variantById(st.product, st.variantId) || firstSellable(st.product);
  }

  function localHas(st, color, size) {
    var meta = { c: optPos(st.product, ['color', 'colour']), s: optPos(st.product, ['size']) };
    return (st.product.variants || []).some(function (v) {
      if (!v.available) return false;
      if (meta.s && size && v['option' + meta.s] !== size) return false;
      if (meta.c && color && v['option' + meta.c] !== color) return false;
      return true;
    });
  }

  /* GATE 1 of 3. Off Lebanon there is no such thing as a preorder substitute,
     so nothing is ever sourced from a twin and no amber ever appears. */
  function twinHas(st, color, size) {
    if (!isLB()) return false;
    if (!st.twin || !window.MDTwin) return false;
    return !!window.MDTwin.findVariant(st.twin, color, size);
  }

  function paint(row) {
    var handle = row.getAttribute('data-handle');
    var st = state[handle];
    if (!st || !row.__meta) return;

    var product = st.product;
    var meta = row.__meta;

    // Colours: every colour the product lists, so a shopper can see the range.
    var colours = [];
    if (meta.colorPos) {
      optValues(product, meta.colorPos).forEach(function (c) { if (colours.indexOf(c) === -1) colours.push(c); });
      (product.variants || []).forEach(function (v) {
        var c = v['option' + meta.colorPos];
        if (c && colours.indexOf(c) === -1) colours.push(c);
      });
    }
    if (!st.color && colours.length) st.color = colours[0];

    var sizes = meta.sizePos ? optValues(product, meta.sizePos) : [];
    if (!st.size && sizes.length) st.size = sizes[0];

    // Decide where the current selection can be served from.
    var fromLocal = localHas(st, st.color, st.size);
    var fromTwin = !fromLocal && twinHas(st, st.color, st.size);
    st.useTwin = fromTwin;

    if (fromLocal) {
      var lv = (product.variants || []).filter(function (v) {
        if (!v.available) return false;
        if (meta.sizePos && st.size && v['option' + meta.sizePos] !== st.size) return false;
        if (meta.colorPos && st.color && v['option' + meta.colorPos] !== st.color) return false;
        return true;
      })[0];
      if (lv) st.variantId = lv.id;
    }

    var variant = activeVariant(st);

    /* GATE 2 of 3. Worldwide never reads the word preorder, even though the
       products it buys are preorder-tagged behind the scenes. */
    var preorder = isLB() && (st.useTwin || isPreorderProduct(product));

    var priceEl = row.querySelector('.mdw-row__price');
    if (priceEl) priceEl.textContent = variant ? W.formatMoney(cents(variant.price)) : '';

    var img = row.querySelector('.mdw-row__img img');
    if (img) {
      var srcProduct = st.useTwin && st.twin ? st.twin : product;
      var cPos = (st.useTwin && st.twin && window.MDTwin)
        ? window.MDTwin.colorPos(st.twin)
        : meta.colorPos;
      var src = sized(imageForColor(srcProduct, cPos, st.color, variant), 500);
      if (src && img.getAttribute('src') !== src) img.src = src;
    }

    var colorEl = row.querySelector('.mdw-row__color');
    if (colorEl) colorEl.textContent = st.color || '—';
    var sizeEl = row.querySelector('.mdw-row__size');
    if (sizeEl) sizeEl.textContent = st.size || '—';

    // Colour swatches
    var cWrap = row.querySelector('.mdw-colors');
    if (cWrap) {
      cWrap.innerHTML = '';
      colours.forEach(function (c) {
        var b = el('button', 'mdw-swatch-btn');
        b.type = 'button';
        b.title = c;
        b.setAttribute('aria-label', c);

        var anyLocal = localHas(st, c, null);
        var anyTwin = twinHas(st, c, null);
        if (!anyLocal && anyTwin) b.classList.add('is-preorder');
        if (!anyLocal && !anyTwin) b.classList.add('is-gone');
        if (c === st.color) b.classList.add('is-selected');

        var s = swatchStyle(c, null);
        if (s.kind === 'image') {
          b.classList.add('mdw-swatch-btn--img');
          b.style.backgroundImage = 'url("' + s.value + '")';
        } else {
          b.style.background = s.value;
        }

        b.addEventListener('click', function () {
          if (b.classList.contains('is-gone')) return;
          st.color = c;
          paint(row);
        });

        cWrap.appendChild(b);
      });
    }

    // Size buttons. In Lebanon, sold out locally but available from the twin
    // stays clickable and turns amber. Elsewhere that branch cannot be reached.
    var sWrap = row.querySelector('.mdw-sizes');
    if (sWrap) {
      sWrap.innerHTML = '';
      sizes.forEach(function (sz) {
        var b = el('button', 'mdw-size');
        b.type = 'button';
        b.textContent = sz;
        b.setAttribute('data-size', sz);

        var hasLocal = localHas(st, st.color, sz);
        var hasTwin = !hasLocal && twinHas(st, st.color, sz);

        if (!hasLocal && hasTwin) b.classList.add('is-preorder');
        if (!hasLocal && !hasTwin) {
          b.classList.add('is-unavailable');
          b.disabled = true;
        }
        if (sz === st.size) b.classList.add('is-selected');

        b.addEventListener('click', function () {
          if (b.disabled) return;
          st.size = sz;
          paint(row);
        });

        sWrap.appendChild(b);
      });
    }

    /* GATE 3 of 3. This element only exists in Lebanon (see build), so the
       whole block is inert elsewhere — belt and braces. */
    var stock = row.querySelector('.mdw-stock');
    if (stock && isLB()) {
      stock.className = 'mdw-stock';
      if (preorder) {
        stock.classList.add('mdw-stock--pre');
        stock.innerHTML = SVG_CLOCK + '<span>' + mdT('md_wishlist_page.stock_preorder', 'Preorder 15–20 business days') + '</span>';
      } else if (!variant) {
        stock.classList.add('mdw-stock--out');
        stock.innerHTML = SVG_CLOCK + '<span>' + mdT('md_wishlist_page.stock_out', 'Out of stock') + '</span>';
      } else {
        stock.classList.add('mdw-stock--in');
        stock.innerHTML = SVG_CHECK + '<span>' + mdT('md_wishlist_page.stock_in', 'In stock 2–5 business days') + '</span>';
      }
    }

    var addBtn = row.querySelector('.mdw-add');
    var buyBtn = row.querySelector('.mdw-buy');
    if (addBtn) addBtn.disabled = !variant;
    if (buyBtn) {
      buyBtn.textContent = preorder ? mdT('md_wishlist_page.preorder_now', 'Preorder now') : mdT('md_wishlist_page.buy_it_now', 'Buy it now');
      buyBtn.disabled = !variant;
    }

    row.classList.toggle('is-preorder-mode', !!preorder);
  }

  /* -------------------------------------------------------------- summary */

  function renumber() {
    var grid = document.getElementById('md-wishlist-grid');
    if (!grid) return;
    var rows = grid.querySelectorAll('.mdw-row');
    for (var i = 0; i < rows.length; i++) {
      var n = rows[i].querySelector('.mdw-row__num');
      if (n) n.textContent = String(i + 1);
    }
  }

  function updateSummary() {
    var grid = document.getElementById('md-wishlist-grid');
    var summary = document.getElementById('md-wishlist-summary');
    var totalEl = document.getElementById('md-wishlist-summary-total');
    var countEl = document.getElementById('md-wishlist-summary-count');
    var headCount = document.getElementById('md-wishlist-total');
    if (!grid) return;

    var rows = grid.querySelectorAll('.mdw-row');
    var n = rows.length;
    var sum = 0;
    var known = false;

    for (var i = 0; i < n; i++) {
      var st = state[rows[i].getAttribute('data-handle')];
      if (!st) continue;
      var v = activeVariant(st);
      if (v) { sum += cents(v.price); known = true; }
    }

    if (headCount) {
      headCount.textContent = n === 1 ? mdT('md_wishlist_page.count_one', '1 item') : mdT('md_wishlist_page.count_other', '{count} items').replace('{count}', n);
      headCount.hidden = n === 0;
    }
    if (countEl) countEl.textContent = n === 1 ? mdT('md_wishlist_page.count_one', '1 item') : mdT('md_wishlist_page.count_other', '{count} items').replace('{count}', n);
    if (totalEl) totalEl.textContent = known ? W.formatMoney(sum) : '—';
    if (summary) summary.hidden = n === 0;

    // MD 7 Sep 2026 - the empty state is hidden up front by render() whenever the wishlist
    // holds any handles at all. If every one of those rows was then dropped by the market
    // gate, the page would be left completely blank, so re-show it off the live row count.
    var emptyEl = document.getElementById('md-wishlist-empty');
    if (emptyEl) emptyEl.hidden = n !== 0;
  }

  /* --------------------------------------------------------------- render */

  function shapeOf(p) {
    return JSON.stringify((p.options || []).map(function (o) { return o.values || []; }));
  }

  function revalidate(row, handle, mine, hadCache) {
    fetch('/products/' + encodeURIComponent(handle) + '.js')
      .then(function (r) {
        if (r && r.status === 404) { var g = new Error('missing'); g.missing = true; throw g; }
        if (!r || !r.ok) throw new Error('unavailable');
        return r.json();
      })
      .then(function (product) {
        if (mine !== token) return;
        var c = C();
        if (c) c.put(handle, product);

        var old = state[handle] && state[handle].product;
        if (hadCache && old && shapeOf(old) === shapeOf(product)) {
          state[handle].product = product;
          paint(row);
        } else {
          build(row, product);
        }
        renumber();
        updateSummary();
      })
      .catch(function (err) {
        if (mine !== token) return;
        var c = C();

        if (err && err.missing) {
          if (c) c.drop(handle);
          delete state[handle];
          stub(row, handle, mdT('md_wishlist_page.not_available_region', 'Not available in your region right now.'));
        } else if (!hadCache) {
          stub(row, handle, mdT('md_wishlist_page.could_not_load', 'Could not load right now. Still saved.'));
        }

        renumber();
        updateSummary();
      });
  }

  function render() {
    var root = document.getElementById('md-wishlist-root');
    var grid = document.getElementById('md-wishlist-grid');
    if (!root || !grid) return;

    market = root.getAttribute('data-market') === 'LB' ? 'LB' : 'WW';

    var empty = document.getElementById('md-wishlist-empty');
    var summary = document.getElementById('md-wishlist-summary');
    var handles = W.get();
    var mine = ++token;
    var cache = C();

    order = handles.slice();
    grid.innerHTML = '';

    if (!handles.length) {
      updateSummary();
      if (empty) empty.hidden = false;
      if (summary) summary.hidden = true;
      return;
    }
    if (empty) empty.hidden = true;

    handles.forEach(function (handle) {
      var cached = cache ? cache.get(handle) : null;
      var row;

      if (cached) {
        row = el('div', 'mdw-row');
        row.setAttribute('data-handle', handle);
        grid.appendChild(row);
        build(row, cached);
      } else {
        row = skeleton(handle);
        grid.appendChild(row);
      }

      var isFresh = cached && cache && cache.fresh(handle);
      if (!isFresh) revalidate(row, handle, mine, !!cached);
    });

    renumber();
    updateSummary();
  }

  /* --------------------------------------------------------------- events */

  document.addEventListener('click', function (event) {
    var btn = event.target.closest && event.target.closest('[data-wishlist-remove]');
    if (!btn) return;
    event.preventDefault();

    var handle = btn.getAttribute('data-wishlist-remove');
    var row = btn.closest('.mdw-row');

    W.remove(handle, 'card');
    delete state[handle];
    var i = order.indexOf(handle);
    if (i > -1) order.splice(i, 1);

    if (!row) { render(); return; }

    var grid = row.parentElement;
    row.classList.add('is-removing');

    var finish = function () {
      row.remove();
      renumber();
      updateSummary();
      var empty = document.getElementById('md-wishlist-empty');
      if (grid && !grid.querySelectorAll('.mdw-row').length && empty) empty.hidden = false;
    };

    if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) finish();
    else setTimeout(finish, 200);
  });

  function addToCart(variantId) {
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] })
    }).then(function (r) {
      if (!r.ok) throw new Error('add-failed');
      return r.json();
    });
  }

  function note(msg) {
    var host = document.getElementById('md-wishlist-root');
    if (!host) return;
    var old = host.querySelector('.mdw-note');
    if (old) old.remove();
    var n = el('p', 'mdw-note');
    n.textContent = msg;
    var summary = document.getElementById('md-wishlist-summary');
    (summary && summary.parentNode ? summary.parentNode : host).appendChild(n);
    setTimeout(function () { if (n.parentNode) n.remove(); }, 6000);
  }

  document.addEventListener('click', function (event) {
    var t = event.target;
    if (!t.closest) return;

    var add = t.closest('.mdw-add');
    var buy = t.closest('.mdw-buy');
    if (!add && !buy) return;

    var rowEl = (add || buy).closest('.mdw-row');
    if (!rowEl) return;
    var st = state[rowEl.getAttribute('data-handle')];
    if (!st) return;

    var variant = activeVariant(st);
    if (!variant) return;

    var btn = add || buy;
    btn.classList.add('is-busy');

    addToCart(variant.id)
      .then(function () {
        if (buy) { window.location.href = '/checkout'; return; }
        btn.classList.remove('is-busy');
      })
      .catch(function () {
        btn.classList.remove('is-busy');
        note(mdT('md_wishlist_page.size_not_added', 'That size could not be added. Please open the product page and try there.'));
      });
  });

  document.addEventListener('click', function (event) {
    var all = event.target.closest && event.target.closest('#md-wishlist-buy-all');
    if (!all) return;
    event.preventDefault();

    var items = [];
    var grid = document.getElementById('md-wishlist-grid');
    var rows = grid ? grid.querySelectorAll('.mdw-row') : [];

    for (var i = 0; i < rows.length; i++) {
      var st = state[rows[i].getAttribute('data-handle')];
      if (!st) continue;
      var v = activeVariant(st);
      if (v) items.push({ id: v.id, quantity: 1 });
    }

    if (!items.length) { note(mdT('md_wishlist_page.nothing_available', 'Nothing here is available to add right now.')); return; }

    all.classList.add('is-busy');
    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify({ items: items })
    })
      .then(function (r) { if (!r.ok) throw 0; return r.json(); })
      .then(function () { window.location.href = '/checkout'; })
      .catch(function () {
        all.classList.remove('is-busy');
        note(mdT('md_wishlist_page.checkout_failed', 'We could not start checkout. Please try again.'));
      });
  });

  document.addEventListener('wishlist:change', function (event) {
    if (event.detail && event.detail.source === 'card') return;
    render();
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', render);
  } else {
    render();
  }

  window.MDWishlistPage = { render: render };
})();
